<main class="legal">

  <!-- Hero -->
  <section class="legal-hero">
    <div class="legal-hero-inner">
      <span class="legal-eyebrow">Terms</span>
      <h1>Terms of Use</h1>
      <p class="legal-meta">Last updated <time datetime="2024-07-14">July 14, 2024</time></p>
    </div>
  </section>

  <!-- Body: TOC sidebar + content -->
  <section class="legal-body">
    <div class="legal-body-inner">

      <aside class="legal-toc" aria-label="Table of contents">
        <details class="legal-toc-collapse">
          <summary class="legal-toc-title">Contents</summary>
          <nav class="legal-toc-nav">
            <a href="terms#acceptance">1. Acceptance of Terms</a>
            <a href="terms#use-of-services">2. Use of Our Services</a>
            <a href="terms#your-content">3. Your Content</a>
            <a href="terms#intellectual-property">4. Intellectual Property</a>
            <a href="terms#disclaimers">5. Disclaimers and Limitation of Liability</a>
            <a href="terms#indemnification">6. Indemnification</a>
            <a href="terms#governing-law">7. Governing Law</a>
            <a href="terms#changes">8. Changes to These Terms</a>
            <a href="terms#contact-us">9. Contact Us</a>
          </nav>
        </details>
      </aside>

      <article class="legal-content">

        <p class="legal-summary">
          Welcome to GitCharger Inc. ("GitCharger," "we," "us," or "our"). We help
          Electric Vehicle (EV) users locate chargers around them. By accessing or
          using our app and website, you agree to be bound by these Terms of Use.
          Please read them carefully.
        </p>

        <h2 id="acceptance">1. Acceptance of Terms</h2>
        <p>
          By using our services, you agree to comply with and be bound by these Terms
          of Use and our Privacy Policy. If you do not agree to these terms, please
          do not use our services.
        </p>

        <h2 id="use-of-services">2. Use of Our Services</h2>
        <ul>
          <li>
            <b>Eligibility:</b> You must be at least 13 years old to use our services.
            If you are under 18, you must have your parent or guardian's permission.
          </li>
          <li>
            <b>Account Registration:</b> You may need to create an account to access
            certain features. You agree to provide accurate and complete information
            during registration and to keep your account up-to-date.
          </li>
          <li>
            <b>Prohibited Activities:</b> You agree not to use our services for any
            unlawful purpose or in any way that could harm our app, website, its users,
            or any third party, including hacking, spamming, and distributing malware.
          </li>
        </ul>

        <!-- DRAFT - added 2026-05-13. See docs/privacy-terms-review.md. Counsel review required. -->
        <h2 id="your-content">3. Your Content</h2>
        <p>
          <b>"Your Content"</b> means anything you post, upload, or submit through GitCharger,
          including community chat messages, station ratings and reviews, photos, and
          station contributions (new stations, edits to existing ones).
        </p>
        <ul>
          <li>
            <b>Acceptable use.</b> You agree not to post content that is:
            <ul>
              <li>Harassing, abusive, threatening, hateful, or discriminatory</li>
              <li>Spam, off-topic, or unrelated to charging stations</li>
              <li>Misleading, deceptive, or fraudulent</li>
              <li>Pornographic, sexually explicit, or inappropriate for a public driver community</li>
              <li>Doxxing or revealing other people's personal information</li>
              <li>Promoting illegal activity, or itself illegal under applicable law</li>
              <li>Infringing any intellectual property right</li>
            </ul>
          </li>
          <li>
            <b>Our right to remove.</b> We may remove Your Content at any time, with or
            without notice, if we believe it violates these Terms or our policies. We may
            also suspend or terminate accounts that repeatedly post prohibited content.
          </li>
          <li>
            <b>What happens when you delete your account.</b> Your Content is removed from
            public view or dissociated from your personal information. Some content
            (e.g., aggregate station availability reports) may remain in anonymized form
            to maintain the integrity of community data.
          </li>
          <li>
            <b>Reporting violations.</b> Use the <b>Report</b> action on any message,
            rating, or station to flag inappropriate content. We review reports and act
            when warranted. To report copyright infringement, see our DMCA Policy
            (coming soon).
          </li>
        </ul>

        <h2 id="intellectual-property">4. Intellectual Property</h2>
        <ul>
          <li>
            <b>Ownership:</b> All content, features, and functionality on our app and
            website are the exclusive property of GitCharger or its licensors. This includes
            text, graphics, logos, and software.
          </li>
          <li>
            <b>License:</b> We grant you a limited, non-exclusive, non-transferable
            license to access and use our services for personal, non-commercial use.
            You may not reproduce, distribute, modify, or create derivative works from
            our content without our express written permission.
          </li>
        </ul>

        <h2 id="disclaimers">5. Disclaimers and Limitation of Liability</h2>
        <ul>
          <li>
            <b>Disclaimers:</b> Our services are provided "as is" and "as available"
            without warranties of any kind, either express or implied. We do not
            guarantee that our services will be uninterrupted or error-free.
          </li>
          <li>
            <b>Limitation of Liability:</b> To the fullest extent permitted by law,
            GitCharger shall not be liable for any indirect, incidental, special,
            consequential, or punitive damages, or any loss of profits or revenues,
            whether incurred directly or indirectly, or any loss of data, use, goodwill,
            or other intangible losses resulting from (a) your use of our services;
            (b) any unauthorized access to or use of our servers and any personal
            information stored therein; and (c) any interruption or cessation of
            transmission to or from our services.
          </li>
        </ul>

        <h2 id="indemnification">6. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless GitCharger, its affiliates, and
          their respective officers, directors, employees, and agents from and against
          any claims, liabilities, damages, losses, and expenses, including reasonable
          attorneys' fees, arising out of or in any way connected with your access to
          or use of our services or your violation of these Terms of Use.
        </p>

        <h2 id="governing-law">7. Governing Law</h2>
        <p>
          These Terms of Use and any disputes arising out of or related to them will
          be governed by the laws of the State of Delaware, without regard to its
          conflict of laws principles.
        </p>

        <h2 id="changes">8. Changes to These Terms</h2>
        <p>
          We may update these Terms of Use from time to time. We will notify you of
          any changes by posting the new Terms of Use on our app and website. Your
          continued use of our services after the changes take effect signifies your
          acceptance of the new terms.
        </p>

        <h2 id="contact-us">9. Contact Us</h2>
        <p>
          If you have any questions about these Terms of Use, please reach us via our
          <a routerLink="/contact">contact page</a>.
        </p>

      </article>

    </div>
  </section>

</main>
